import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Folder, Clock, Activity, CheckCircle, XCircle, Star, Filter, Plus, MoreVertical, Trash2, ExternalLink, TrendingUp, Beaker, Dna, TestTube, FileText, Table, AlertCircle, Hourglass } from 'lucide-react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useTeam } from '../../teams/contexts/TeamContext';
import { listProjects, getProjectStatsBatch, createProject, deleteProject, updateProject, type Project } from '../lib/projects';
import { listJobs, getOdysseyJobStats } from '../../jobs/lib/jobs';
import { toast } from 'sonner';
import { migrateUserToTeams } from '../../teams/lib/teamMigration';

export const WelcomePage: React.FC = () => {
  const { user } = useAuth();
  const { currentTeam, loading: teamLoading, error: teamError, refreshTeams } = useTeam();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState<Record<string, any>>({});
  const [filterType, setFilterType] = useState<'all' | 'starred' | 'unstarred'>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeProjectMenu, setActiveProjectMenu] = useState<string | null>(null);
  const [basePdbFilter, setBasePdbFilter] = useState<'all' | 'has_base_pdb' | 'has_base_cif'>('all');
  const [generationFilter, setGenerationFilter] = useState<'all' | '1-5' | '5-10' | '10+'>('all');
  const [jobStats, setJobStats] = useState({
    total: 0,
    completed: 0,
    running: 0,
    thisWeek: 0
  });
  const filterRef = useRef<HTMLDivElement>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectWorkflowType] = useState<'enzyme' | 'antibody' | 'peptide' | null>('enzyme');
  const [newProjectFileType, setNewProjectFileType] = useState<'pdb' | 'cif'>('pdb');
  const [lastOpenedTimes, setLastOpenedTimes] = useState<Record<string, number>>({});
  const [isMigrating, setIsMigrating] = useState(false);
  const loadingRef = useRef(false); // Prevent duplicate loading calls
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load projects immediately when user is available, don't wait for team context
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user]);

  // Update projects when team changes (for team filtering)
  const currentTeamRef = useRef(currentTeam?.id);
  useEffect(() => {
    // Only reload if team actually changed and we have projects loaded
    if (user && currentTeam && projects.length > 0 && currentTeamRef.current !== currentTeam.id) {
      currentTeamRef.current = currentTeam.id;
      loadProjects();
    } else if (currentTeam) {
      currentTeamRef.current = currentTeam.id;
    }
  }, [currentTeam]);

  // Reload projects when returning to the welcome page
  useEffect(() => {
    // Only reload if we have user and are on the root path and already have some projects loaded
    // This ensures we refresh the list when returning from a project page
    if (user && location.pathname === '/' && projects.length > 0) {
      loadProjects();
    }
  }, [location.pathname, user]);

  // Load last opened times from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('projectLastOpened');
    if (stored) {
      try {
        setLastOpenedTimes(JSON.parse(stored));
      } catch (error) {
        console.error('Error parsing last opened times:', error);
      }
    }
  }, []);

  // Click outside handler for dropdowns and cleanup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
      
      // Close project menu if clicking outside
      const target = event.target as HTMLElement;
      if (!target.closest('.project-menu')) {
        setActiveProjectMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup loading timeout on unmount
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const loadProjects = async () => {
    // Prevent duplicate loading calls
    if (loadingRef.current) {
      return;
    }
    
    try {
      loadingRef.current = true;
      setLoading(true);
      
      // Set timeout to prevent stuck loading state
      loadingTimeoutRef.current = setTimeout(() => {
        if (loadingRef.current) {
          console.warn('Project loading timed out');
          setLoading(false);
          loadingRef.current = false;
          toast.error('Loading took too long. Please refresh the page.');
        }
      }, 15000); // 15 second timeout
      
      // Load projects - use currentTeam.id if available, otherwise load all user projects
      const teamId = currentTeam?.id;
      const projectsData = await listProjects(teamId);
      setProjects(projectsData);
      setLoading(false); // Stop showing loading state once projects are loaded
      
      // Load job stats and project stats in parallel (non-blocking)
      Promise.all([
        // Load overall Odyssey job stats using count-only queries
        getOdysseyJobStats().then(setJobStats),
        
        // Load stats for all projects in batch (much more efficient)
        getProjectStatsBatch(projectsData.map(p => p.id))
          .then(stats => {
            setProjectStats(stats);
          })
          .catch(error => {
            console.error('Error loading project stats:', error);
          })
      ]).catch(error => {
        console.error('Error loading additional data:', error);
      });
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
      setLoading(false);
    } finally {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      loadingRef.current = false;
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    let matchesStarFilter = true;
    if (filterType === 'starred') matchesStarFilter = project.starred === true;
    if (filterType === 'unstarred') matchesStarFilter = project.starred !== true;
    
    let matchesBasePdbFilter = true;
    if (basePdbFilter !== 'all' && projectStats[project.id]) {
      const hasBasePdb = projectStats[project.id]?.hasBasePdb || false;
      const hasBaseCif = projectStats[project.id]?.hasBaseCif || false;
      if (basePdbFilter === 'has_base_pdb') matchesBasePdbFilter = hasBasePdb;
      else if (basePdbFilter === 'has_base_cif') matchesBasePdbFilter = hasBaseCif;
    }
    
    let matchesGenerationFilter = true;
    if (generationFilter !== 'all' && projectStats[project.id]) {
      const maxGen = projectStats[project.id].maxGeneration || 0;
      if (generationFilter === '1-5') matchesGenerationFilter = maxGen >= 1 && maxGen <= 5;
      else if (generationFilter === '5-10') matchesGenerationFilter = maxGen > 5 && maxGen <= 10;
      else if (generationFilter === '10+') matchesGenerationFilter = maxGen > 10;
    }
    
    return matchesSearch && matchesStarFilter && matchesBasePdbFilter && matchesGenerationFilter;
  });

  const sortedProjects = [...filteredProjects]
    .sort((a, b) => {
      const aLastOpened = lastOpenedTimes[a.id] || 0;
      const bLastOpened = lastOpenedTimes[b.id] || 0;
      
      // If both have been opened, sort by last opened time (most recent first)
      if (aLastOpened > 0 && bLastOpened > 0) {
        return bLastOpened - aLastOpened;
      }
      
      // If one has been opened and one hasn't, opened projects come first
      if (aLastOpened > 0 && bLastOpened === 0) {
        return -1; // A (opened) comes before B (not opened)
      }
      
      if (aLastOpened === 0 && bLastOpened > 0) {
        return 1; // B (opened) comes before A (not opened)
      }
      
      // If neither has been opened, sort by created_at (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const getStatusIcon = (stats: any) => {
    if (!stats) return null;
    
    if (stats.runningJobs > 0) {
      return <Activity className="w-4 h-4 text-blue-500 animate-pulse" />;
    } else if (stats.failedJobs > 0) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    } else if (stats.completedJobs > 0) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else {
      return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    if (!currentTeam) {
      toast.error('Please wait for team context to load');
      return;
    }
    
    try {
      const newProjectId = await createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
        workflow_type: newProjectWorkflowType || 'enzyme',
        team_id: currentTeam.id
      });
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectFileType('pdb');
      // Workflow type is now always 'enzyme'
      
      // Treat creating a project the same as opening it - set last opened time
      const newTimes = {
        ...lastOpenedTimes,
        [newProjectId]: Date.now()
      };
      setLastOpenedTimes(newTimes);
      localStorage.setItem('projectLastOpened', JSON.stringify(newTimes));
      
      // Set the project context and file type preference before navigating
      sessionStorage.setItem('currentProjectId', newProjectId);
      sessionStorage.setItem(`project_${newProjectId}_fileType`, newProjectFileType);
      navigate(`/projects/${newProjectId}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleDeleteProject = async (projectId: string, projectName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to project
    
    if (confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      try {
        await deleteProject(projectId);
        toast.success('Project deleted successfully');
        loadProjects(); // Reload the projects list
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
    setActiveProjectMenu(null);
  };

  const handleMigration = async () => {
    setIsMigrating(true);
    try {
      const success = await migrateUserToTeams();
      if (success) {
        toast.success('Successfully set up teams! Refreshing...');
        await refreshTeams();
      } else {
        toast.error('Failed to set up teams. Please try again.');
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('An error occurred during setup.');
    } finally {
      setIsMigrating(false);
    }
  };

  // Show team setup prompt if no team is available
  if (!teamLoading && !currentTeam && user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-6 py-12">
            <div className="bg-white rounded-sm shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-sm flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-gray-700" />
              </div>
              <h2 className="text-2xl font-normal text-gray-900 mb-3">
                Welcome to the New Teams Feature!
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                We've added team collaboration features to anthrogen:drift. 
                Let's set up your personal workspace to get started.
              </p>
              
              {teamError && (
                <div className="mb-6 p-4 bg-red-50 rounded-sm text-red-600 text-sm">
                  <p className="font-medium mb-1">Error: {teamError}</p>
                  {teamError.includes('migrations') && (
                    <p className="text-xs">
                      If you're an administrator, please run the team migration SQL in your Supabase dashboard.
                    </p>
                  )}
                </div>
              )}
              
              <button
                onClick={handleMigration}
                disabled={isMigrating}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 text-white font-medium rounded-sm hover:from-gray-800 hover:to-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMigrating ? (
                  <>
                    <div className="animate-spin rounded-sm h-4 w-4 border-b-2 border-white mr-2"></div>
                    Setting up...
                  </>
                ) : (
                  'Set Up My Workspace'
                )}
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                This will create a personal team and organize your existing projects.
              </p>
            </div>
          </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Welcome Section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-6xl lg:text-7xl font-light mb-8 tracking-tight">
              Welcome to{' '}
              <span className="text-black font-semibold">
                anthrogen:drift
              </span>
            </h1>
            <p className="text-2xl lg:text-3xl text-gray-600 mb-12 font-light max-w-3xl mx-auto">
              Design proteins with the power of AI
            </p>
          </div>
          
          {/* Stats Cards */}
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-sm shadow-sm hover:shadow-lg border border-gray-100 p-4 transition-all duration-300 hover:-translate-y-1 animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-normal text-gray-600">Total Generations</h3>
                <div className="p-1.5 bg-gray-100 rounded-sm">
                  <Activity className="h-4 w-4 text-gray-700" />
                </div>
              </div>
              <p className="text-2xl font-light text-gray-900">{jobStats.total}</p>
              <p className="text-xs text-gray-500 mt-1 font-medium">All time</p>
            </div>

            <div className="bg-white rounded-sm shadow-sm hover:shadow-lg border border-gray-100 p-4 transition-all duration-300 hover:-translate-y-1 animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-normal text-gray-600">Completed</h3>
                <div className="p-1.5 bg-gray-100 rounded-sm">
                  <CheckCircle className="h-4 w-4 text-gray-600" />
                </div>
              </div>
              <p className="text-2xl font-light text-gray-900">{jobStats.completed}</p>
              <p className="text-xs text-gray-600 mt-1 font-medium">
                {jobStats.total > 0 ? Math.round((jobStats.completed / jobStats.total) * 100) : 0}% success
              </p>
            </div>

            <div className="bg-white rounded-sm shadow-sm hover:shadow-lg border border-gray-100 p-4 transition-all duration-300 hover:-translate-y-1 animate-fade-in" style={{animationDelay: '0.3s'}}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-normal text-gray-600">Running</h3>
                <div className="p-1.5 bg-gray-100 rounded-sm">
                  <Clock className="h-4 w-4 text-gray-600 animate-pulse" />
                </div>
              </div>
              <p className="text-2xl font-light text-gray-900">{jobStats.running}</p>
              <p className="text-xs text-gray-600 mt-1 font-medium">In progress</p>
            </div>

            <div className="bg-white rounded-sm shadow-sm hover:shadow-lg border border-gray-100 p-4 transition-all duration-300 hover:-translate-y-1 animate-fade-in" style={{animationDelay: '0.4s'}}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-normal text-gray-600">This Week</h3>
                <div className="p-1.5 bg-gray-100 rounded-sm">
                  <TrendingUp className="h-4 w-4 text-gray-700" />
                </div>
              </div>
              <p className="text-2xl font-light text-gray-900">{jobStats.thisWeek}</p>
              <p className="text-xs text-gray-500 mt-1 font-medium">New jobs</p>
            </div>
          </div>
          
          <div className="text-center">
            {/* Search Bar with Filter and Add Button */}
            <div className="max-w-4xl mx-auto flex items-center space-x-4 animate-fade-in" style={{animationDelay: '0.5s'}}>
              <div className="relative flex-1 group">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-blue-500" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-sm shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg transition-all duration-200 placeholder:text-gray-400"
                />
              </div>
              
              {/* Filter Dropdown */}
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className="flex items-center space-x-2 px-5 py-3.5 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Star className={`w-5 h-5 transition-colors ${
                    filterType === 'all' ? 'text-gray-400' : 
                    filterType === 'starred' ? 'text-yellow-400 fill-yellow-400' : 
                    'text-gray-400'
                  }`} />
                  {filterType === 'starred' && <span className="text-gray-700 font-medium">Starred</span>}
                  {filterType === 'unstarred' && <span className="text-gray-700 font-medium">Unstarred</span>}
                </button>
                
                {showFilterMenu && (
                  <div className="absolute top-full mt-3 right-0 bg-white border border-gray-100 rounded-sm shadow-xl z-10 min-w-[180px] overflow-hidden animate-scale-in">
                    <button
                      onClick={() => { setFilterType('all'); setShowFilterMenu(false); }}
                      className={`flex items-center gap-3 w-full text-left px-4 py-3 transition-colors ${filterType === 'all' ? 'bg-gray-100 text-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <Star className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">All Projects</span>
                    </button>
                    <button
                      onClick={() => { setFilterType('starred'); setShowFilterMenu(false); }}
                      className={`flex items-center gap-3 w-full text-left px-4 py-3 transition-colors ${filterType === 'starred' ? 'bg-gray-100 text-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span className="font-medium">Starred Only</span>
                    </button>
                    <button
                      onClick={() => { setFilterType('unstarred'); setShowFilterMenu(false); }}
                      className={`flex items-center gap-3 w-full text-left px-4 py-3 transition-colors ${filterType === 'unstarred' ? 'bg-gray-100 text-gray-800' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <Star className="w-4 h-4 text-gray-300" />
                      <span className="font-medium">Unstarred Only</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Add Project Button */}
              <button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center space-x-2 px-6 py-3.5 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-sm hover:from-gray-800 hover:to-gray-900 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Plus className="w-5 h-5" />
                <span className="font-semibold">Add Project</span>
              </button>
            </div>
            
            {/* Tag Filters */}
            <div className="max-w-4xl mx-auto mt-4 flex items-center gap-4 justify-center flex-wrap">
              {/* Base PDB Filters */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Type:</span>
                <button
                  onClick={() => setBasePdbFilter('all')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors ${
                    basePdbFilter === 'all' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setBasePdbFilter('has_base_pdb')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors flex items-center gap-1 ${
                    basePdbFilter === 'has_base_pdb' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Beaker className="h-3 w-3" />
                  Has Base PDB
                </button>
                <button
                  onClick={() => setBasePdbFilter('has_base_cif')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors flex items-center gap-1 ${
                    basePdbFilter === 'has_base_cif' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Beaker className="h-3 w-3" />
                  Has Base CIF
                </button>
              </div>
              
              {/* Generation Filters */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Generations:</span>
                <button
                  onClick={() => setGenerationFilter('all')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors ${
                    generationFilter === 'all' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setGenerationFilter('1-5')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors ${
                    generationFilter === '1-5' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  1-5
                </button>
                <button
                  onClick={() => setGenerationFilter('5-10')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors ${
                    generationFilter === '5-10' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  5-10
                </button>
                <button
                  onClick={() => setGenerationFilter('10+')}
                  className={`px-3 py-1 text-sm rounded-sm transition-colors ${
                    generationFilter === '10+' 
                      ? 'bg-gray-200 text-gray-800 font-medium' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  10+
                </button>
              </div>
            </div>
          </div>

          {/* Recent Projects */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Recent Projects</h2>
            </div>

            {loading ? (
              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-sm p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="h-6 bg-gray-100 rounded-sm w-1/3 mb-2 shimmer" />
                        <div className="h-4 bg-gray-100 rounded-sm w-2/3 mb-3 shimmer" style={{animationDelay: '0.1s'}} />
                        <div className="flex items-center space-x-6">
                          <div className="h-3 bg-gray-100 rounded-sm w-24 shimmer" style={{animationDelay: '0.2s'}} />
                          <div className="h-3 bg-gray-100 rounded-sm w-28 shimmer" style={{animationDelay: '0.3s'}} />
                          <div className="h-3 bg-gray-100 rounded-sm w-20 shimmer" style={{animationDelay: '0.4s'}} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-100 rounded-sm w-20 ml-auto shimmer" />
                        <div className="flex space-x-4">
                          <div className="h-3 bg-gray-100 rounded-sm w-24 shimmer" style={{animationDelay: '0.5s'}} />
                          <div className="h-3 bg-gray-100 rounded-sm w-24 shimmer" style={{animationDelay: '0.6s'}} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedProjects.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-sm shadow-sm">
                <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No projects yet</h3>
                <p className="text-gray-500 mb-4">Get started by creating your first project</p>
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors"
                >
                  Create Project
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {sortedProjects.slice(0, 5).map((project, index) => {
                  const stats = projectStats[project.id];
                  return (
                    <div
                      key={project.id}
                      onClick={() => {
                        // Update last opened time
                        const newTimes = {
                          ...lastOpenedTimes,
                          [project.id]: Date.now()
                        };
                        setLastOpenedTimes(newTimes);
                        localStorage.setItem('projectLastOpened', JSON.stringify(newTimes));
                        navigate(`/projects/${project.id}`);
                      }}
                      className="relative bg-white rounded-sm p-6 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100 hover:border-gray-200 hover:-translate-y-1 animate-fade-in"
                      style={{animationDelay: `${0.1 * index}s`}}
                    >
                      <div className="flex items-start justify-between overflow-visible">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-800 group-hover:text-gray-800 transition-colors">
                              {project.name}
                            </h3>
                            
                            {/* Base PDB Tag */}
                            {stats && stats.hasBasePdb && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-200 text-gray-700">
                                <Beaker className="h-3 w-3" />
                                Has Base PDB
                              </span>
                            )}

                            {/* Base CIF Tag */}
                            {stats && stats.hasBaseCif && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-200 text-gray-700">
                                <Beaker className="h-3 w-3" />
                                Has Base CIF
                              </span>
                            )}
                            
                            {/* Generation Count Tag */}
                            {stats && stats.maxGeneration > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-200 text-gray-700">
                                <TrendingUp className="h-3 w-3" />
                                Gen {stats.maxGeneration}
                              </span>
                            )}
                            
                            {project.starred && <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />}
                            {getStatusIcon(stats)}
                          </div>
                          
                          {project.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                              {project.description}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <span>Created: {formatDate(project.created_at)}</span>
                            <span>Last opened: {formatDate(
                              lastOpenedTimes[project.id] 
                                ? new Date(lastOpenedTimes[project.id]).toISOString() 
                                : project.updated_at
                            )}</span>
                            <span>Owned by: {project.owner?.display_name || project.owner?.email?.split('@')[0] || (project.owner?.first_name && project.owner?.last_name ? `${project.owner.first_name} ${project.owner.last_name}` : project.owner?.first_name) || 'Unknown'}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-8">
                          <div className="flex items-center space-x-4">
                            {stats ? (
                              <>
                                <div className="flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 rounded-sm border border-gray-200">
                                  <Hourglass className="w-4 h-4 text-gray-700" />
                                  <span className="text-sm font-bold text-gray-900">{stats.maxGeneration || 0}</span>
                                </div>
                                <div className="flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 rounded-sm border border-gray-200">
                                  <FileText className="w-4 h-4 text-gray-700" />
                                  <span className="text-sm font-bold text-gray-900">{stats.totalEntries || 0}</span>
                                </div>
                                <div className="flex items-center space-x-2 bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 rounded-sm border border-gray-200">
                                  <Table className="w-4 h-4 text-gray-700" />
                                  <span className="text-sm font-bold text-gray-900">{stats.totalDataEntries || 0}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="animate-pulse">
                                  <div className="h-8 w-16 bg-gray-200 rounded-sm"></div>
                                </div>
                                <div className="animate-pulse">
                                  <div className="h-8 w-16 bg-gray-200 rounded-sm"></div>
                                </div>
                                <div className="animate-pulse">
                                  <div className="h-8 w-16 bg-gray-200 rounded-sm"></div>
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Three dots menu */}
                          <div className="relative project-menu overflow-visible">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveProjectMenu(activeProjectMenu === project.id ? null : project.id);
                              }}
                              className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-500" />
                            </button>
                            
                            {activeProjectMenu === project.id && (
                              <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-sm shadow-lg z-50 min-w-[160px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/projects/${project.id}`);
                                  }}
                                  className="flex items-center space-x-2 w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  <span>Open Project</span>
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await updateProject(project.id, { starred: !project.starred });
                                      toast.success(project.starred ? 'Project unstarred' : 'Project starred');
                                      loadProjects(); // Reload to update UI
                                      setActiveProjectMenu(null);
                                    } catch (error) {
                                      console.error('Error toggling star:', error);
                                      toast.error('Failed to update project');
                                    }
                                  }}
                                  className="flex items-center space-x-2 w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-700"
                                >
                                  <Star className={`w-4 h-4 ${project.starred ? 'text-gray-600 fill-gray-600' : 'text-gray-400'}`} />
                                  <span>{project.starred ? 'Unstar Project' : 'Star Project'}</span>
                                </button>
                                <button
                                  onClick={(e) => handleDeleteProject(project.id, project.name, e)}
                                  className="flex items-center space-x-2 w-full px-4 py-2 text-left hover:bg-red-50 text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Delete Project</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      
      {/* Create Project Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Project</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="My Protein Design Project"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base File Type *
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newProjectFileType === 'pdb'}
                      onChange={() => setNewProjectFileType('pdb')}
                      className="w-5 h-5 rounded border-gray-300 focus:ring-2 focus:ring-offset-0 mr-2"
                      style={{
                        accentColor: '#000000'
                      }}
                    />
                    <span className="text-sm text-gray-700">PDB (.pdb)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newProjectFileType === 'cif'}
                      onChange={() => setNewProjectFileType('cif')}
                      className="w-5 h-5 rounded border-gray-300 focus:ring-2 focus:ring-offset-0 mr-2"
                      style={{
                        accentColor: '#000000'
                      }}
                    />
                    <span className="text-sm text-gray-700">CIF (.cif, .mmcif)</span>
                  </label>
                </div>
              </div>
              
              {/* Workflow Type is now automatically set to 'enzyme' */}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Describe the goals or purpose of this project..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewProjectName('');
                  setNewProjectDescription('');
                  // newProjectWorkflowType is const, no need to reset
                  setNewProjectFileType('pdb');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
